<template>
  <section class="app-finance">
    <integration-steps :current-step="4" />

    <h4 class="app-finance__title">
      Defina a sua conta bancária para recebimento das consultas
    </h4>

    <article class="app-finance__wrapper">
      <header class="app-finance__header">
        <h5 class="app-finance__subtitle">Dados Bancários</h5>

        <div
          v-show="!ui.isLoading || ui.isSaving"
          class="app-finance__edit-icon"
          @click="ui.isEditing = !ui.isEditing"
        >
          <dr-icon
            v-if="!ui.isEditing"
            :class="[
              'app-prices__office-price-actions--icon',
              'app-prices__office-price-actions--icon-reverse'
            ]"
            name="edit"
            color="gray"
          />

          <dr-icon
            v-if="ui.isEditing"
            name="widgetTick"
            color="rgb(86, 189, 210)"
            class="app-prices__office-price-actions--icon"
          />
        </div>
      </header>

      <div class="app-finance__col">
        <dl class="app-finance__bank-details">
          <dt class="app-finance__bank-field">
            Tipo:
          </dt>
          <dd class="app-finance__bank-value">
            <i
              v-if="ui.isLoading"
              class="icon loading"
            />

            <template v-else>
              <div
                v-click-outside="() => ui.showAccountTypeList = false"
                :class="[
                  ui.showAccountTypeList && 'weekday-interval__select--active',
                  !ui.isEditing && 'app-finance__disabled'
                ]"
                class="weekday-interval__select weekday-interval__select--hour app-finance__select"
              >
                <div
                  :class="ui.showAccountTypeList && 'weekday-interval__pick--open'"
                  class="weekday-interval__pick"
                  @click="
                    ui.showAccountTypeList = !ui.showAccountTypeList;
                    scrollToOption(
                      `select-account-type`,
                      `select-account-type-item-${
                        accountTypeList.findIndex(t => t.name === bankDetails['account_type']) || 0
                      }`,
                      ui.showAccountTypeList
                    );
                  "
                >
                  <span>
                    {{
                      accountTypeList.find(t => t.name === bankDetails['account_type'])
                        ? accountTypeList.find(t => t.name === bankDetails['account_type']).label
                        : ''
                    }}
                  </span>
                  <i class="icon icon-arrow-down" />
                </div>

                <ul
                  v-if="ui.showAccountTypeList"
                  id="select-account-type"
                  class="weekday-interval__select-list app-finance__select-list"
                >
                  <li
                    v-for="(type, index) in accountTypeList"
                    :key="`select-account-type-${index}`"
                    :id="`select-account-type-item-${index}`"
                    :class="
                      (accountTypeList.findIndex(t => t.name === bankDetails['account_type']) === index) &&
                      'weekday-interval__select-item--selected'
                    "
                    class="weekday-interval__select-item"
                    @click="
                      bankDetails['account_type'] = type.name;
                      ui.showAccountTypeList = false;
                    "
                  >
                    {{ type.label }}
                  </li>
                </ul>
              </div>
            </template>
          </dd>

          <dt class="app-finance__bank-field">
            Banco:
          </dt>
          <dd class="app-finance__bank-value">
            <i
              v-if="ui.isLoading"
              class="icon loading"
            />

            <template v-else>
              <div
                v-click-outside="() => ui.showBanksList = false"
                :class="[
                  ui.showBanksList && 'weekday-interval__select--active',
                  !ui.isEditing && 'app-finance__disabled',
                  'weekday-interval__select',
                  'weekday-interval__select--hour',
                  'app-finance__select',
                  'app-finance__select--no-pad'
                ]"
              >
                <input
                  :value="ui.inputBankName"
                  :class="[
                    ui.showBanksList && 'weekday-interval__pick--open',
                    !ui.isEditing && 'app-finance__disabled'
                  ]"
                  class="weekday-interval__pick weekday-interval__select app-finance__input"
                  @focus="$event.target.select()"
                  @input="
                    ui.inputBankName = $event.target.value;
                    chosenBank.id = null;
                  "
                  @click="
                    ui.showBanksList = !ui.showBanksList;
                    scrollToOption(
                      `select-bank-list`,
                      `select-bank-list-item-${
                        banksList
                          .findIndex(bank => bank.id === bankDetails.bank.id)
                      }`,
                      ui.showBanksList
                    );
                  "
                >
                <ul
                  v-if="ui.showBanksList"
                  id="select-bank-list"
                  class="weekday-interval__select-list app-finance__select-list"
                >
                  <li
                    v-for="(bank, index) in filteredBankList"
                    :key="`select-bank-list-${index}`"
                    :id="`select-bank-list-item-${index}`"
                    :class="
                      (banksList.findIndex(bank => bank.id === bankDetails.bank.id) === index) &&
                      'weekday-interval__select-item--selected'
                    "
                    class="weekday-interval__select-item"
                    @click="
                      chosenBank = { ...bank };
                      ui.showBanksList = false;
                    "
                  >
                    {{ bank.number }} - {{ bank.name }}
                  </li>
                </ul>
              </div>
            </template>
          </dd>

          <dt class="app-finance__bank-field">
            Agência:

            <span
              data-tooltip="Número da sua agência bancária sem o dígito"
              class="tooltip tooltip-top page-info inline-block !ml-0"
            >
              <dr-icon
                class="v-icon"
                color="silver"
                name="info"
              />
            </span>
          </dt>
          <dd class="app-finance__bank-value">
            <i
              v-if="ui.isLoading"
              class="icon loading"
            />

            <template v-else>
              <input
                type="text"
                maxlength="4"
                :value="bankDetails['agency']"
                :class="(!ui.isEditing || ui.isLoading) && 'app-finance__disabled'"
                class="weekday-interval__select app-finance__input"
                @input="
                  $set(bankDetails, 'agency', numbersOnly($event.target.value));
                  $forceUpdate();
                  watchIsDisabled();
                "
              >
            </template>
          </dd>

          <dt class="app-finance__bank-field">
            Conta:
          </dt>
          <dd class="app-finance__bank-value">
            <i
              v-if="ui.isLoading"
              class="icon loading"
            />

            <template v-else>
              <input
                type="text"
                maxlength="15"
                :value="bankDetails['account']"
                :class="(!ui.isEditing || ui.isLoading) && 'app-finance__disabled'"
                class="weekday-interval__select app-finance__input"
                @input="
                  $set(bankDetails, 'account', hyphenate($event.target.value));
                  $forceUpdate();
                  watchIsDisabled();
                "
              >
            </template>
          </dd>

          <dt
            v-if="'account_name' in bankDetails && bankDetails['account_name']"
            class="app-finance__bank-field"
          >
            Nome:
          </dt>
          <dd
            v-if="'account_name' in bankDetails && bankDetails['account_name']"
            class="app-finance__bank-value"
          >
            {{ bankDetails['account_name'] }}
          </dd>

          <dt
            v-if="'account_document_type' in bankDetails && bankDetails['account_document_type']"
            class="app-finance__bank-field"
          >
            {{ bankDetails["account_document_type"] }}:
          </dt>
          <dd
            v-if="'account_document_type' in bankDetails && bankDetails['account_document_type']"
            class="app-finance__bank-value"
          >
            <template v-if="bankDetails['account_document_type'] === 'CPF'">
              {{ doctor.user.cpf | TO_CPF }}
            </template>

            <template v-if="bankDetails['account_document_type'] === 'CNPJ'">
              {{ bankDetails['account_document_number'] | TO_CNPJ }}
            </template>
          </dd>
        </dl>
      </div>

      <div class="app-finance__col">
        <ul class="app-finance__list">
          <li class="app-finance__item">
            <dr-icon
              name="widgetTick"
              fill="gradient"
              class="app-finance__item-icon"
            />

            <p class="app-finance__item-text">
              Os recebimentos das consultas do Marketplace Dr. Conecta serão creditados
              automaticamente em sua conta bancária após 30 dias da data de
              agendamento da consulta.
            </p>
          </li>

          <li class="app-finance__item">
            <dr-icon
              name="widgetTick"
              fill="gradient"
              class="app-finance__item-icon"
            />

            <p class="app-finance__item-text">
              Você pode acompanhar a previsão de recebimento destes pagamentos
              no extrato financeiro, na área de relatórios.
            </p>
          </li>

          <li class="app-finance__item">
            <dr-icon
              name="widgetTick"
              fill="gradient"
              class="app-finance__item-icon"
            />

            <p class="app-finance__item-text">
              A liquidação destes pagamentos só pode ocorrer em conta bancária
              de sua titularidade, não sendo possível alterar nome e CPF.
            </p>
          </li>

          <li class="app-finance__item">
            <dr-icon
              name="widgetTick"
              fill="gradient"
              class="app-finance__item-icon"
            />

            <p class="app-finance__item-text">
              Caso queira cadastrar uma conta Pessoa Jurídica, envie um e-mail para
              <a href="mailto:atendimento@drconecta.com.br">atendimento@drconecta.com.br</a>
              para habilitarmos essa funcionalidade.
            </p>
          </li>
        </ul>
      </div>
    </article>

    <footer class="app-finance__footer">
       <button
        :disabled="ui.isDisabled || ui.isLoading || ui.isSaving"
        class="btn btn-primary app-integration__submit"
        @click="save()"
      >
        <span v-if="!ui.isLoading || ui.isSaving">Próximo</span>
        <dr-loader v-else size="sm" color="white" />
      </button>
    </footer>
  </section>
</template>

<script>
import integrationSteps from './integrationSteps'
import { mapGetters } from 'vuex'

export default {
  name: 'AppIntegrationFinance',

  components: {
    integrationSteps
  },

  props: {
    doctor: {
      type: Object,
      required: true,
      default: () => {}
    }
  },

  data () {
    return {
      banksList: [],
      bankDetails: null,
      chosenBank: {},
      accountTypeList: null,
      ui: {
        isDisabled: true,
        isLoading: false,
        isEditing: false,
        isSaving: false,
        showAccountTypeList: false,
        showBanksList: false,
        inputBankName: ''
      }
    }
  },

  computed: {
    ...mapGetters({
      appIntegration: 'getAppIntegration'
    }),

    filteredBankList () {
      if (this.banksList.length > 0) {
        return this.banksList.filter((bank) => {
          const split = this.ui.inputBankName
            ? this.ui.inputBankName.split('-')
            : ['']
          const search = this.fn_REMOVE_ACCENTS(split[split.length - 1].toLowerCase()).trim()

          return this.fn_REMOVE_ACCENTS(bank.name.toLowerCase())
            .indexOf(search) !== -1
        })
      }

      return []
    }
  },

  watch: {
    bankDetails: {
      deep: true,
      immediate: false,
      handler () {
        if (!('account' in this.bankDetails)) {
          const {
            agency,
            agency_digit, // eslint-disable-line
            account_number, // eslint-disable-line
            account_document_number, // eslint-disable-line
            account_document_type, // eslint-disable-line
            account_digit // eslint-disable-line
          } = this.bankDetails

          delete this.bankDetails['agency']
          delete this.bankDetails['agency_digit']
          delete this.bankDetails['account_number']
          delete this.bankDetails['account_digit']
          // delete this.bankDetails['account_document_type']
          // delete this.bankDetails['account_document_number']

          this.bankDetails.agency = agency // `${agency}-${agency_digit}` // eslint-disable-line
          this.bankDetails.account = `${account_number}-${account_digit}` // eslint-disable-line
          this.chosenBank = { ...this.bankDetails.bank }
          this.ui.inputBankName = `${this.chosenBank.number} - ${this.chosenBank.name}`
        }

        this.watchIsDisabled()
      }
    },

    chosenBank: {
      deep: true,
      immediate: false,
      handler () {
        if ('id' in this.chosenBank && this.chosenBank.id) {
          this.bankDetails.bank = { ...this.chosenBank }
          this.ui.inputBankName = `${this.chosenBank.number} - ${this.chosenBank.name}`
        }

        if ('id' in this.chosenBank && !this.chosenBank.id) {
          this.bankDetails.bank.name = null
          this.bankDetails.bank.id = null
          this.bankDetails.bank.number = null
        }
      }
    }
  },

  created () {
    this.bankDetails = this.buildBankDetails()

    this.accountTypeList = [
      { label: 'Corrente', name: 'CORRENTE' }
      // { label: 'Poupança', name: 'POUPANCA' }
    ]

    this.ui.isLoading = true
    this.fn_SHOW_PROGRESS()
    this.getBanks()
      .then(res => (this.banksList = res.data))
      .then(this.getBankDetails)
      .then((res) => {
        const data = { ...res.data }

        for (const entry in data) {
          if (data[entry]['is_active']) {
            this.bankDetails = { ...data[entry] }
            return
          }
        }

        this.bankDetails = this.buildBankDetails()
        this.ui.isEditing = true
      })
      .catch(err => console.trace(err))
      .finally(() => {
        this.ui.isLoading = false
        this.fn_HIDE_PROGRESS()
      })
  },

  methods: {
    getBanks () {
      return this.$http
        .get('/bank', this.$store.getters.getAuthHeader)
    },

    getBankDetails () {
      return this.$http
        .get('/doctor/bank_detail', this.$store.getters.getAuthHeader)
    },

    buildBankDetails () {
      return {
        id: null,
        bank: {
          id: null,
          number: null,
          name: null
        },
        agency: null,
        account: null,
        'account_name': null,
        'account_type': null,
        'is_active': null
      }
    },

    scrollToOption (where, to, isOpen = false) {
      if (isOpen) {
        this.$nextTick(() => {
          const list = document.querySelector(`#${where}`) || null
          const item = document.querySelector(`#${to}`) || null

          if (list && item) {
            list.scrollTop = item.offsetTop
          }
        })
      }
    },

    hyphenate (value) {
      if (value.length >= 2) {
        const noHyphen = value.replace('-', '')
        const account = noHyphen.substr(0, noHyphen.length - 1)
        const digit = noHyphen.substr(-1, 1)

        return `${this.numbersOnly(account)}-${digit.replace(/[^A-Za-z0-9]/gmi, '').toUpperCase()}`
      }

      return this.numbersOnly(value)
    },

    numbersOnly (value) {
      return value.replace(/\D/gmi, '').toUpperCase()
    },

    watchIsDisabled () {
      const keys = Object.keys(this.bankDetails)
        .filter(key => key !== 'id' && key !== 'is_active' && key !== 'bank')

      // const validateProps = keys
      //   .map(key => this.bankDetails[key])
      //   .every(item => !!item)

      const validateBank = Object.entries(this.bankDetails.bank)
        .every(item => !!item[1])

      const validateAccount = [this.bankDetails.account]
        .every(item => /([A-Za-z0-9]+)-(\d|[A-Za-z]{1})/g.test(item))

      // this.ui.isDisabled = !(validateProps && validateBank && validateAccount)
      this.ui.isDisabled = !(validateBank && validateAccount)
    },

    acceptIntegration () {
      if (!this.appIntegration['is_active']) {
        const url = `/doctor/app_integration/${this.appIntegration.id}/is_active`
        const data = {
          'update_is_active': {
            isActive: 1
          }
        }

        return this.$http.put(url, data, this.$store.getters.getAuthHeader)
      }

      return true
    },

    getAppIntegration () {
      this.$http
        .get('/doctor/app_integration', this.$store.getters.getAuthHeader)
        .then(res => this.$store.dispatch('setAppIntegration', res.data['app_integration']))
    },

    findErrorMessage (object) {
      let errorMessage = null

      const recursiveSearch = (object) => {
        for (let value in object) {
          if (value === 'errors') {
            errorMessage = object[value]
            break
          } else {
            recursiveSearch(object[value])
          }
        }
      }

      if (Array.isArray(object)) {
        object.forEach((element) => recursiveSearch(element))
      } else {
        recursiveSearch(object)
      }

      return errorMessage
    },

    handleError (err) {
      const msg = (typeof err !== 'object')
        ? err
        : ('errors' in err)
          ? this.findErrorMessage(err.errors)
          : 'Ocorreu um erro.'

      this.$store.dispatch('setModalAlert', {
        title: 'Erro',
        body: Array.isArray(msg) ? msg[0] : msg,
        size: 'md',
        active: true
      })
    },

    save () {
      const id = this.bankDetails.id ? `/${this.bankDetails.id}` : ''
      const url = `/doctor/bank_detail${id}`
      const method = id ? 'put' : 'post'
      const headers = this.$store.getters.getAuthHeader.headers
      const agency = this.bankDetails.agency
      const account = this.bankDetails.account.split('-')

      const data = {
        'bank_detail': {
          agency: agency,
          agencyDigit: 0,
          accountNumber: account[0],
          accountDigit: account[1],
          accountType: this.bankDetails['account_type'],
          accountName: id ? this.bankDetails['account_name'] : this.doctor.user.name,
          bank: parseInt(this.bankDetails.bank.id),
          isActive: 1
        }
      }

      this.isSaving = true
      this.fn_SHOW_PROGRESS()
      this.$http({ url, method, headers, data })
        .then(() => this.acceptIntegration())
        .then(() => this.getAppIntegration())
        .then((res) => {
          this.$store.dispatch('setModalAlert', {
            title: 'Sucesso',
            body: 'Dados bancários cadastrados com sucesso.',
            size: 'md',
            active: true,
            callback: () => this.$router.push({ name: 'DoctorAppIntegrationHealthPlace' })
          })
        })
        .catch(err => this.handleError(err))
        .finally(() => {
          this.isSaving = false
          this.fn_HIDE_PROGRESS()
        })
    }
  }
}
</script>
